<template>
  <section v-if="fields" class="c-article-share" :style="rootStyle">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <div class="c-article-share__inner">
        <div class="c-article-share__list">
          <div class="c-article-share__list-item" v-for="item in fields?.list" :key="item.id">
            <Icon :svg="item?.fields?.icon?.value?.svgCode" />
            <JssRichText class="c-article-share__list-item-desc" :field="item?.fields?.desc" />
          </div>
        </div>
        <button
          class="c-article-share__button"
          type="button"
          :aria-label="shareAriaLabel"
          @click="handleShare"
        >
          <Icon :svg="fields?.shareIcon?.value.svgCode" />
        </button>
      </div>
    </ContentGrid>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { ArticleShareProps } from '@/types/components/ArticleShare';
import utils from '@/utils';
import { shareToLinkedIn } from '@/utils/dom-utils';

const props = defineProps<ArticleShareProps>();
const fields = computed(() => props.fields);

const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});
const shareLabel = computed(() => fields.value?.shareLabel?.value || 'Share article');
const configuredShareUrl = computed(() => fields.value?.shareLink?.value?.href || '');

const shareAriaLabel = computed(() => shareLabel.value || 'Share article');

const getShareUrl = () => {
  if (configuredShareUrl.value) return configuredShareUrl.value;
  if (typeof window === 'undefined') return '';
  return window.location.href;
};

const handleShare = () => {
  const url = getShareUrl();
  shareToLinkedIn(url);
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-article-share {
  width: 100%;
  background: $white;

  &__inner {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 40px;
    align-items: center;
    gap: 16px;
    min-height: 64px;
    border-bottom: 1px solid #f0f0f0;
  }

  &__list {
    display: flex;
    align-items: center;
    min-width: 0;
    color: #6f8f72;
    gap: 16px;

    &-item {
      display: flex;
      align-items: center;
      gap: 4px;
      svg {
        width: 20px;
        height: 20px;
        fill: transparent;
      }
      &-desc {
        font-size: 14px;
        line-height: 1.4;
        color: #656565;
      }
    }
  }

  &__button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    justify-self: end;
    width: 40px;
    height: 40px;
    padding: 0;
    color: #111111;
    background: transparent;
    border: 0;
    cursor: pointer;

    svg {
      width: 32px;
      height: 32px;
    }

    &:hover,
    &:focus-visible {
      color: #739b78;
      outline: none;
    }
  }

  @include tablet {
    &__inner {
      grid-template-columns: minmax(0, 1fr) 48px;
      min-height: 68px;
    }

    &__list {
      display: flex;
      align-items: center;
      min-width: 0;
      color: #6f8f72;
      gap: 16px;

      &-item {
        display: flex;
        align-items: center;
        gap: 4px;
        svg {
          width: 20px;
          height: 20px;
          fill: transparent;
        }
        &-desc {
          font-size: 14px;
          line-height: 1.4;
          color: #656565;
        }
      }
    }

    &__button {
      width: 48px;
      height: 48px;

      svg {
        width: 38px;
        height: 38px;
      }
    }
  }
}
</style>
